import type { FilterResult } from '../../services/filter';
/**
 * Filters the data using Chebyshev filtering concept.
 *
 * This uses direct multiplication instead of Math.pow.
 * Doing direct multiplication proves to be faster than Math.pow
 *
 * @module
 */
export default class ChebyshevFilter {
  /**
   * Filters the data
   * @function
   * @param {float} frequency
   * @param {float} fc
   * @param {int} filterOrder
   * @param {float} passbandRipple
   * @returns {FilterResult}
   */
  filterOne(frequency: number, fc: number, filterOrder: number, passbandRipple: number): FilterResult {
    const er = Math.sqrt(Math.pow(10, passbandRipple / 10) - 1),
      x = frequency / fc;
    let t = 0;

    switch (filterOrder) {
      case 1: {
        t = x;
        break;
      }
      case 2: {
        t = 2 * x * x - 1;
        break;
      }
      case 3: {
        t = 4 * x * x * x - 3 * x;
        break;
      }
      case 4: {
        t = 8 * x * x * x * x - 8 * x * x + 1;
        break;
      }
      case 5: {
        t = 16 * x * x * x * x * x - 20 * x * x * x + 5 * x;
        break;
      }
      case 6: {
        t = 32 * x * x * x * x * x * x - 48 * x * x * x * x + 18 * x * x - 1;
        break;
      }
      case 7: {
        t = 64 * x * x * x * x * x * x * x - 112 * x * x * x * x * x + 56 * x * x * x - 7 * x;
        break;
      }
    }

    const numerator = 1,
      denominator = 1 + er * er * t * t,
      ysquared = numerator / denominator,
      y = Math.abs(Math.sqrt(ysquared)),
      z = (20 * Math.log(y)) / Math.log(10);

    return { filterAmplitude: z, filterAttenuation: y };
  }
}
